import { useEffect, useRef, useState } from 'react';
import { get } from 'lodash';
import { toast } from '@chargebee/sting-react';
import { buildLaConfirmationSubmission } from '@bbk/retention-sdk/internal';
import { type RestMultiRowSurveyCard } from '@bbkAdminUtils/api-client/rest-cards-types';
import { type RestExperience } from '@bbkAdminUtils/api-client/rest-experiences-api';
import {
  RestModalAction,
  type RestModalCard,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { type ExecuteEventsHandler } from '@bbkFunnel/events';
import { getModalCtaIdMap } from '@bbkFunnelUtils/cards-utils';
import {
  LA_CONFIRMATION_UNAVAILABLE,
  resolveLaConfirmation,
} from '@bbkFunnelUtils/la-confirmation';

type Props = {
  fullInfo: Pick<RestExperience, 'session' | 'version' | 'layout'>;
  modal: RestModalCard;
  response: RestMultiRowSurveyCard['provides'];
  open?: boolean;
  executeEvents: ExecuteEventsHandler;
  isLifecycle?: boolean;
};

// Keep the LA's ownership, submission lifecycle and presentation policy together.
export const useLaConfirmation = ({
  fullInfo,
  modal,
  response,
  open,
  executeEvents,
  isLifecycle,
}: Props) => {
  const {
    session,
    version,
    layout: { cards },
  } = fullInfo;
  const confirmation = resolveLaConfirmation(cards, modal.name);
  const submitted = useRef(false);
  const [isSubmitted, setSubmitted] = useState(false);
  useEffect(() => {
    submitted.current = false;
    setSubmitted(false);
  }, [modal.name, open]);

  // A consumed action must not fall through to ordinary modal acceptance.
  const handleAction = (action: RestModalForwards): boolean => {
    if (
      confirmation.status === 'invalid' ||
      action.action === RestModalAction.open_offer_confirmation
    ) {
      toast({ title: LA_CONFIRMATION_UNAVAILABLE, variant: 'danger' });
      return true;
    }
    if (confirmation.status !== 'ready') return false;
    if (!open || action.action !== RestModalAction.link || submitted.current)
      return true;

    submitted.current = true;
    setSubmitted(true);
    executeEvents(
      buildLaConfirmationSubmission(confirmation, action, {
        session,
        version,
        app_id: get(fullInfo, 'user.app_id', null),
        user_id: get(fullInfo, 'user.user.id', null),
        surveyState: response,
        isLifecycle,
        fallbackCtaId: getModalCtaIdMap(cards)[modal.name],
      })
    );
    return true;
  };

  const card: RestModalCard =
    confirmation.status === 'ready'
      ? {
          ...confirmation.confirmationModal,
          requires: {
            ...confirmation.confirmationModal.requires,
            hideReturnBtn: false,
            forwards: {
              ...confirmation.confirmationModal.requires.forwards,
              disabled: isSubmitted,
            },
          },
        }
      : modal;

  return { card, handleAction };
};
